/**
 * Typed access to the product contract. Every other module reads the contract
 * through here so there is exactly one place that knows its shape.
 *
 * The JSON under src/generated/product/ is copied verbatim from ../product by
 * scripts/generate.mjs — never edit it here; edit the contract.
 */
import screensJson from '@/generated/product/screens.json'
import flowsJson from '@/generated/product/flows.json'
import tokensJson from '@/generated/product/design-tokens.json'
import {
  INTERSTITIAL_SCREEN_IDS,
  SCREEN_IDS,
  type InterstitialScreenId,
  type RoutableScreenId,
  type ScreenId,
  type ScreenState,
} from '@/generated/screens'

export type { InterstitialScreenId, RoutableScreenId, ScreenId, ScreenState }
export { INTERSTITIAL_SCREEN_IDS, SCREEN_IDS }

export interface ScreenSpec {
  id: ScreenId
  title: string
  purpose: string
  components: string[]
  navigatesTo: ScreenId[]
  auth: 'public' | 'required' | 'any'
  surfaces: string[]
  /** No route and no URL; presented over whatever is already open. */
  interstitial?: boolean
}

export const screens = screensJson.screens as ScreenSpec[]
export const screenById = new Map<ScreenId, ScreenSpec>(screens.map((s) => [s.id, s]))
export const navRoots = screensJson.navRoots as RoutableScreenId[]
export const startScreen = screensJson.start as RoutableScreenId
export const startAuthenticatedScreen = screensJson.startAuthenticated as RoutableScreenId

/** Every state a screen must implement, straight from flows.json. */
export const screenStates = flowsJson.states as Record<ScreenId, string[]>

export function statesFor(id: ScreenId): readonly string[] {
  return screenStates[id] ?? ['ready']
}

/** Guard used by the router in dev to catch navigation the contract forbids. */
export function canNavigate(from: ScreenId, to: ScreenId): boolean {
  if ((navRoots as ScreenId[]).includes(to)) return true // global nav chrome, not a screen edge
  return screenById.get(from)?.navigatesTo.includes(to) ?? false
}

export const designTokens = tokensJson
